import React, { forwardRef, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Tooltip as MuiTooltip,
  TooltipProps as MuiTooltipProps,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslate } from 'src/locales';
import { humanizeLabelKey } from 'src/utils/change-case';
import { maxLine } from 'src/theme/styles';
import { useTheme } from '@mui/material/styles';
import { PhoneInput } from '../phone-input';
import EncryptUtils from 'src/utils/EncryptUtils';
import { executeUserActions } from 'src/services/service-handlers/PrivateService';
import { toast } from 'src/components/snackbar';
import { useLocation } from 'react-router';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { formatDateTime } from 'src/utils/helper';
import { getFormFieldErrorMessage } from 'src/utils/ErrorUtils';
import { buildModalRowSchema, getModalRowErrors } from './table-modal-row-schema';

interface Option {
  label: string;
  value: any;
}

interface Condition {
  fieldName: string;
  fieldValue: any | any[];
}

interface Column {
  name: string;
  label: string;
  type: string;
  options?: Option[];
  hide_in_table?: boolean;
  conditionalRender?: boolean;
  condition?: Condition;
  heading?: string;
  sub_heading?: string;
  helper_text?: string;
  is_heading_only_block?: boolean;
  disabled?: boolean;
  required?: boolean;
  defaultValue?: any;
  columns?: any;
  delete_sql?: any;
  validation_schema?: string;
}

interface RHFTableInputProps {
  columns: Column[];
  name: string;
  disabled: boolean;
  delete_sql?: any;
}

interface TooltipProps extends Omit<MuiTooltipProps, 'children'> {
  children: React.ReactElement;
  title: string;
  infoButton?: boolean;
}

const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(({ children, ...props }, ref) => (
  <MuiTooltip
    ref={ref}
    {...props}
    componentsProps={{
      tooltip: {
        sx: {
          fontSize: '16px',
        },
      },
    }}
  >
    <Box
      component="span"
      display={props?.infoButton ? 'inline' : 'inline-block'}
      width={props?.infoButton ? 'min-content' : '100%'}
    >
      {children}
    </Box>
  </MuiTooltip>
));

Tooltip.displayName = 'Tooltip';

export function RHFTableInput({ columns, name, disabled, delete_sql }: RHFTableInputProps) {
  const { getValues, setValue, control, clearErrors, formState: { errors } } = useFormContext();
  const formWatch = useWatch({ control, name });
  const [rows, setRows] = useState<any[]>(formWatch || []);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [backupRow, setBackupRow] = useState<any | null>(null);
  const [open, setOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [isDraft, setIsDraft] = useState(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [modalErrors, setModalErrors] = useState<Record<string, string>>({});
  const location = useLocation();
  const module_name = location.pathname.split('/')[1];
  const theme = useTheme();
  const { t } = useTranslate('form');

  /** Columns visible for the current row (used for validation: only validate visible fields) */
  const getVisibleColumnsForRow = (rowValues: Record<string, any>) =>
    columns.filter((col) => {
      if (!col.conditionalRender || !col.condition) return true;
      const { fieldName, fieldValue } = col.condition;
      const current = rowValues?.[fieldName];
      return Array.isArray(fieldValue)
        ? fieldValue.includes(current)
        : String(current) === String(fieldValue);
    });

  useEffect(() => {
    setRows(formWatch || []);
  }, [formWatch]);

  /* helper to create empty row */
  const buildEmptyRow = () =>
    columns.reduce(
      (acc, col) => {
        acc[col.name] =
          col.type === 'table_input'
            ? []
            : col.type === 'select' && col.options?.length
              ? col.options[0].value
              : '';
        return acc;
      },
      {} as Record<string, any>
    );

  /* ------------------------------------------------ */
  /* ----- util to keep local + RHF in sync  (NEW) --- */
  /* ------------------------------------------------ */
  const updateField = (field: string, value: any) => {
    if (editIndex === null) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setValue(`${name}[${editIndex}].${field}`, value, { shouldDirty: true, shouldValidate: false }); // < added
  };

  const handleOpen = () => {
    const draft = buildEmptyRow();
    const newRows = [...rows, draft];

    setRows(newRows);
    setValue(name, newRows, { shouldValidate: false });
    setFormData(draft);
    setEditIndex(newRows.length - 1);
    setIsDraft(true);
    setOpen(true);
    setModalErrors({});
  };

  const handleEdit = (index: number) => {
    setBackupRow(rows[index]);
    setFormData(rows[index]);
    setEditIndex(index);
    setIsDraft(false);
    setOpen(true);
  };

  const handleClose = () => {
    if (isDraft && editIndex !== null) {
      const updated = rows.filter((_, i) => i !== editIndex);
      setRows(updated);
      setValue(name, updated, { shouldValidate: false, shouldDirty: true });
      clearErrors(`${name}[${editIndex}]`);
    } else if (!isDraft && editIndex !== null && backupRow) {
      const restored = [...rows];
      restored[editIndex] = backupRow;
      setRows(restored);
      setValue(name, restored, { shouldValidate: false, shouldDirty: true });
      clearErrors(`${name}[${editIndex}]`);
    }

    setFormData({});
    setBackupRow(null);
    setEditIndex(null);
    setIsDraft(false);
    setOpen(false);
    setModalErrors({});
    clearErrors(name);
  };

  const handleSave = () => {
    if (editIndex === null) return;
    const rowPath = `${name}[${editIndex}]`;
    const rowValues = getValues(rowPath);

    const visibleColumns = getVisibleColumnsForRow(rowValues ?? {});
    const rowSchema = buildModalRowSchema(
      visibleColumns as import('./table-modal-row-schema').ModalRowColumn[],
      t
    );
    const result = rowSchema.safeParse(rowValues);
    if (!result.success) {
      const errorMap = getModalRowErrors(result.error);
      setModalErrors(errorMap);
      toast.error(t('fillRequiredFields', { defaultValue: 'Please fill in the required fields.' }));
      return;
    }
    const currentRow = result.data;
    const updated = [...rows];
    updated[editIndex] = currentRow;

    setRows(updated);
    setValue(name, updated, { shouldValidate: false });
    setModalErrors({});
    clearErrors(`${name}[${editIndex}]`);
    clearErrors(name);
    setFormData({});
    setBackupRow(null);
    setEditIndex(null);
    setIsDraft(false);
    setOpen(false);
  };

  /* -------------------- change handlers (MODIFIED) -------------------- */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    updateField(e.target.name, e.target.value); // < modified
    if (modalErrors[e.target.name]) {
      setModalErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[e.target.name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    updateField(e.target.name as string, e.target.value); // < modified
    if (e.target.name && modalErrors[e.target.name]) {
      setModalErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[e.target.name as string];
        return newErrors;
      });
    }
  };

  const handleDeleteRows = async () => {
    if (selectedRows.length === 0) return;

    // Check if delete_sql and delete_id_field exist
    const isDeleteSqlValid = delete_sql && delete_sql.delete_id_field;

    // Prepare IDs for server deletion only if delete_sql is valid
    const idsToDelete = isDeleteSqlValid
      ? selectedRows
          .map((index) => rows[index][delete_sql.delete_id_field])
          .filter((id) => id !== undefined && id !== null)
      : [];

    // Check if any selected row is a locally added row (no DB id)
    const hasNewRows = !isDeleteSqlValid
      ? true // if delete_sql invalid, consider all as new/local rows
      : selectedRows.some((index) => !rows[index][delete_sql.delete_id_field]);

    const reason = 'User deleted';

    let success = true;

    // If there are valid IDs to delete on the backend, call delete API
    if (idsToDelete.length > 0) {
      success = await handleSoftDelete(idsToDelete, reason);
    }

    // If server deletion succeeded or not needed, remove rows locally
    if (success) {
      const updatedRows = rows.filter((_, index) => !selectedRows.includes(index));
      setRows(updatedRows);
      setValue(name, updatedRows, { shouldValidate: true, shouldDirty: true });
      setSelectedRows([]);
      toast.success('Deleted successfully.');
    }
  };

  /* -------------------------------------------------------------------- */
  const handleSoftDelete = async (deleteIds: number[], reason: string) => {
    try {
      const payload: any = {
        sql_data: delete_sql,
        deleted_ids: `[${deleteIds.join(',')}]`,
        detete_reason: reason,
      };

      // console.log(deleteIds, payload, 'payload form soft deletes');
      const response = await executeUserActions(module_name, {
        tk: EncryptUtils.encryptedData(payload),
      });
      if (!response?.data || !Array.isArray(response.data)) {
        toast.error('Failed to delete.');
        return false;
      }
      toast.success('Deleted Successfully.');
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const evaluateCondition = (col: Column) => {
    if (!col.conditionalRender || !col.condition) return true;
    const { fieldName, fieldValue } = col.condition;
    const current = formData[fieldName];
    return Array.isArray(fieldValue)
      ? fieldValue.includes(current)
      : String(current) === String(fieldValue);
  };

  const renderInput = (col: Column) => {
    const value = formData[col.name] ?? '';
    const fieldError = modalErrors[col.name];
    const hasError = !!fieldError;

    switch (col.type) {
      case 'text':
      case 'text_area':
        return (
          <TextField
            key={col.name}
            name={col.name}
            label={t(col.label, { defaultValue: humanizeLabelKey(col.label || '') })}
            disabled={col.disabled}
            required={col.required}
            value={value}
            type={
              col.validation_schema === 'email'
                ? 'email'
                : col.validation_schema === 'phoneNumber'
                  ? 'tel'
                  : col.validation_schema === 'number' || col.validation_schema === 'floatNumber'
                    ? 'number'
                    : 'text'
            }
            onChange={handleChange}
            fullWidth
            multiline={col.type === 'text_area'}
            rows={col.type === 'text_area' ? 4 : 1}
            margin="dense"
            error={hasError}
            helperText={hasError ? fieldError : ''}
            {...(col.defaultValue !== undefined && { defaultValue: col.defaultValue })}
          />
        );
      case 'select':
        return (
          <TextField
            key={col.name}
            name={col.name}
            label={t(col.label, { defaultValue: humanizeLabelKey(col.label || '') })}
            disabled={col.disabled}
            required={col.required}
            value={value}
            onChange={handleSelectChange}
            select
            fullWidth
            margin="dense"
            error={hasError}
            helperText={hasError ? fieldError : ''}
          >
            {col.options?.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {t(opt.label, { defaultValue: humanizeLabelKey(opt.label || '') })}
              </MenuItem>
            ))}
          </TextField>
        );
      case 'radio_group':
        return (
          <Box key={col.name}>
            <RadioGroup name={col.name} value={value} onChange={handleChange}>
              {col.options?.map((opt) => {
                // Translate "Yes" and "No" labels using translation keys
                let translatedLabel = opt.label;
                if (opt.label === 'Yes') {
                  translatedLabel = t('yes');
                } else if (opt.label === 'No') {
                  translatedLabel = t('no');
                } else {
                  translatedLabel = t(opt.label, { defaultValue: humanizeLabelKey(opt.label || '') });
                }
                return (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio disabled={col.disabled} />}
                    label={translatedLabel}
                  />
                );
              })}
            </RadioGroup>
            {hasError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {fieldError}
              </Typography>
            )}
          </Box>
        );
      case 'phone':
        return (
          <PhoneInput
            key={col.name}
            name={col.name}
            label={t(col.label, { defaultValue: humanizeLabelKey(col.label || '') })}
            fullWidth
            value={value}
            onChange={(v: string) => {
              updateField(col.name, v); // < modified
              if (modalErrors[col.name]) {
                setModalErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors[col.name];
                  return newErrors;
                });
              }
            }}
            error={hasError}
            helperText={hasError ? fieldError : ''}
          />
        );
      case 'table_input':
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h4">{t(col.label, { defaultValue: humanizeLabelKey(col.label || '') })}</Typography>
            <RHFTableInput
              columns={col.columns ?? []}
              name={`${name}[${editIndex ?? 0}].${col.name}`}
              disabled={disabled}
              delete_sql={col.delete_sql ?? {}}
            />
          </Box>
        );
      case 'date_input': // or your type string for date
        return (
          <DatePicker
            label={t(col.label, { defaultValue: humanizeLabelKey(col.label || '') })}
            value={value ? dayjs(value) : null}
            onChange={(date) => {
              if (!date || !dayjs(date).isValid()) {
                updateField(col.name, null);
                return;
              }
              updateField(col.name, date.format('YYYY-MM-DD'));
              if (modalErrors[col.name]) {
                setModalErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors[col.name];
                  return newErrors;
                });
              }
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: 'outlined',
                margin: 'dense',
                required: col.required,
                disabled: col.disabled,
                error: hasError || !!(errors as any)?.[name]?.[editIndex ?? 0]?.[col.name],
                helperText: hasError
                  ? fieldError
                  : (errors as any)?.[name]?.[editIndex ?? 0]?.[col.name]?.message || '',
              },
            }}
          />
        );
      case 'date_time_input': // or your type string for datetime
        return (
          <DateTimePicker
            label={t(col.label, { defaultValue: humanizeLabelKey(col.label || '') })}
            value={value ? dayjs(value) : null}
            onChange={(date) => {
              updateField(col.name, date ? date.toISOString() : null);
              if (modalErrors[col.name]) {
                setModalErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors[col.name];
                  return newErrors;
                });
              }
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: 'outlined',
                margin: 'dense',
                required: col.required,
                disabled: col.disabled,
                error: hasError || !!(errors as any)?.[name]?.[editIndex ?? 0]?.[col.name],
                helperText: hasError
                  ? fieldError
                  : (errors as any)?.[name]?.[editIndex ?? 0]?.[col.name]?.message || '',
              },
            }}
          />
        );
      default:
        return null;
    }
  };

  /* ----------------- UI (unchanged except for comment) ---------------- */
  return (
    <Box>
      <Button
        variant="contained"
        onClick={handleOpen}
        startIcon={<Icon icon="octicon:person-add-24" />}
        disabled={disabled}
        sx={{ mb: 2 }}
      >
        {t('addEntryButtonLabel', { defaultValue: 'Add Entry' })}
      </Button>

      <Dialog maxWidth="xl" fullWidth open={open} onClose={handleClose}>
        <DialogTitle>
          {isDraft
            ? t('addEntryTitle', { defaultValue: 'Add Entry' })
            : t('editEntryTitle', { defaultValue: 'Edit Entry' })}
        </DialogTitle>
        <DialogContent>
          {columns.filter(evaluateCondition).map((col) => (
            <Box key={col.name} sx={{ mt: 2, width: 1 }}>
              {col.heading && (
                <Typography
                  variant="h6"
                  my={col.sub_heading ? 1 : 0}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    ...maxLine({ line: 2, persistent: theme.typography.h6 }),
                  }}
                >
                  {t(col.heading)}{' '}
                  {(col.type === 'radio_group' ||
                    col.type === 'select' ||
                    col.is_heading_only_block) &&
                    col.helper_text && (
                      <Tooltip
                        PopperProps={{ disablePortal: true }}
                        infoButton
                        title={t(col.helper_text)}
                      >
                        <Icon icon="si:info-fill" />
                      </Tooltip>
                    )}
                </Typography>
              )}
              {col.sub_heading && (
                <Typography variant="body2" my={1}>
                  {t(col.sub_heading)}
                </Typography>
              )}
              {renderInput(col)}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            {t('cancelLabel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="contained" onClick={handleSave}>
            {isDraft
              ? t('addButtonLabel', { defaultValue: 'Add' })
              : t('updateButtonLabel', { defaultValue: 'Update' })}
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedRows.length === rows.length}
                  indeterminate={selectedRows.length > 0 && selectedRows.length < rows.length}
                  onChange={(e) => setSelectedRows(e.target.checked ? rows.map((_, i) => i) : [])}
                  disabled={disabled}
                />
              </TableCell>
              {columns
                .filter((c) => !c.hide_in_table)
                .map((c) => (
                  <TableCell key={c.name}>{t(c.label)}</TableCell>
                ))}
              <TableCell
                sx={{
                  position: 'sticky',
                  right: 0,
                  // backgroundColor: theme.palette.background.paper,
                  zIndex: 10,
                }}
              >
                {t('actionsLabel', { defaultValue: 'Actions' })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx} sx={{ width: '100%' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedRows.includes(idx)}
                    onChange={() =>
                      setSelectedRows((prev) =>
                        prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
                      )
                    }
                    disabled={disabled}
                  />
                </TableCell>
                {columns
                  .filter((c) => !c.hide_in_table)
                  .map((c) => (
                    <TableCell key={c.name} sx={{ whiteSpace: 'nowrap' }}>
                      {c.type === 'select' || c.type === 'radio_group'
                        ? t(c.options?.find((opt) => opt.value === row[c.name])?.label ?? '')
                        : c.type === 'date_input' || c.type === 'date_time_input'
                          ? formatDateTime(row[c.name], c.type === 'date_input')
                          : row[c.name]}
                    </TableCell>
                  ))}
                <TableCell
                  sx={{
                    position: 'sticky',
                    right: 0,
                    backgroundColor: theme.palette.background.paper, // Background to match table
                    zIndex: 10, // Ensure it's above other cells
                  }}
                >
                  <IconButton onClick={() => handleEdit(idx)}>
                    <Icon icon="mdi:pencil-outline" />
                  </IconButton>
                  <IconButton
                    onClick={async () => {
                      const reason = 'User deleted';
                      const idToDelete = delete_sql?.delete_id_field
                        ? row[delete_sql.delete_id_field]
                        : null;

                      if (!idToDelete) {
                        // It's a locally added row (no DB id)
                        const updatedRows = rows.filter((_, i) => i !== idx);
                        setRows(updatedRows);
                        setValue(name, updatedRows, { shouldValidate: true, shouldDirty: true });
                        toast.success('Deleted successfully.');
                      } else {
                        // Persistent row - call soft delete API
                        const success = await handleSoftDelete([idToDelete], reason);
                        if (success) {
                          const updatedRows = rows.filter((_, i) => i !== idx);
                          setRows(updatedRows);
                          setValue(name, updatedRows, { shouldValidate: true, shouldDirty: true });
                        }
                      }
                    }}
                  >
                    <Icon icon="ic:baseline-delete" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Validation error display - only when popup is open (RHF validates table only when modal is open) */}
      {errors[name] && open && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {errors[name]?.message?.toString() || 'Please fill in this required table.'}
        </Typography>
      )}

      {selectedRows.length > 0 && (
        <Button
          sx={{ mt: 2 }}
          color="error"
          onClick={handleDeleteRows}
          startIcon={<Icon icon="ic:baseline-delete" />}
          disabled={disabled}
        >
          Delete Selected
        </Button>
      )}
    </Box>
  );
}
